import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { useLoggedInUserStore } from "@/store/LoggedInUserStore";

export default class MsalService {
  constructor() {
    this.config = {
      auth: {
        clientId: MsalClientId, // eslint-disable-line no-undef
        authority: MsalAuthority, // eslint-disable-line no-undef
        redirectUri: MsalRedirectUri, // eslint-disable-line no-undef
        postLogoutRedirectUri: MsalRedirectUri, // eslint-disable-line no-undef
        knownAuthorities: ["ihreapothekenb2c.b2clogin.com"],
      },
      cache: {
        cacheLocation: "localStorage", // critical for Firefox
        storeAuthStateInCookie: true, // helps cross-site scenarios
        claimsBasedCachingEnabled: true,
      },
      system: {
        allowRedirectInIframe: false,
      },
    };

    this.myMsal = new PublicClientApplication(this.config);

    this.loginRequest = {
      scopes: [
        "openid",
        "offline_access",
        "https://ihreapothekenb2c.onmicrosoft.com/d95e0fa5-d738-4657-b150-72d42372ba6f/ReadAppRoles",
      ],
    };

    this.accountId = "";
    this.currentUser = {
      userName: "",
      fullName: "",
      accessToken: "",
      isUserLoggedIn: false,
      permissions: "",
    };

    this.reducerActions = Object.freeze({
      login: "login",
      logout: "logout",
    });
  }

  #initialize = async () => {
    await this.myMsal.initialize();
  };

  #setStore = () => {
    const loggedInUserStore = useLoggedInUserStore();
    loggedInUserStore.setCurrentUser(this.currentUser);
  };

  #setCurrentUser = async (account /**: AccountInfo */) => {
    if (!account) throw new Error("No account provided");
    this.myMsal.setActiveAccount(account);
    this.accountId = account.homeAccountId;

    let accessToken = "";
    try {
      const silent = await this.myMsal.acquireTokenSilent({
        scopes: this.loginRequest.scopes,
        account,
      });
      accessToken = silent.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await this.myMsal.acquireTokenRedirect({
          scopes: this.loginRequest.scopes,
          account,
        });
        return;
      }
      throw e;
    }

    this.currentUser = {
      userName: account?.idTokenClaims?.email ?? "",
      fullName: account?.name ?? "",
      accessToken,
      isUserLoggedIn: true,
      permissions: account?.idTokenClaims?.extension_roles ?? "",
    };
    this.#setStore();
  };

  #handleResponse = async (response) => {
    if (response?.account) {
      await this.#setCurrentUser(response.account);
      return;
    }
    const accounts = this.myMsal.getAllAccounts();
    if (accounts.length === 1) {
      await this.#setCurrentUser(accounts[0]);
      return;
    }
    if (accounts.length > 1) {
      await this.#setCurrentUser(accounts[0]);
      return;
    }
    await this.myMsal.loginRedirect(this.loginRequest);
  };

  #login = async () => {
    if (this.currentUser.isUserLoggedIn === true) {
      if (import.meta.env.VITE_ENVIRONMENT !== "prod") {
        console.warn("Login called but user is already logged in");
      }
      return;
    }

    try {
      const result = await this.myMsal.handleRedirectPromise();
      await this.#handleResponse(result);
    } catch (err) {
      if (import.meta.env.VITE_ENVIRONMENT !== "prod") console.error(err);
      throw err;
    }
  };

  #logout = async () => {
    if (this.currentUser.isLoggedIn === false) {
      Promise.reject(new Error("User is already logged out.")).then((err) => {
        if (import.meta.env.VITE_ENVIRONMENT !== "prod") {
          console.error(err);
        }
      });
    }
    await this.myMsal.logoutRedirect(this.logoutRequest);
    this.currentUser = {
      userName: "",
      accessToken: "",
      isUserLoggedIn: false,
      permissions: "",
    };
    const loggedInUserStore = useLoggedInUserStore();
    loggedInUserStore.setCurrentUser(this.currentUser);
  };

  msalCallReducer = async (action) => {
    try {
      await this.#initialize();
      switch (action) {
        case this.reducerActions.login:
          await this.#login();
          break;
        case this.reducerActions.logout:
          await this.#logout();
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
    } catch (error) {
      if (import.meta.env.VITE_ENVIRONMENT !== "prod") {
        console.error(new Error(`Error in msalCallReducer: ${error.message}`));
      }
    }
  };
}
